import type { SetStateAction, Dispatch } from 'react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { deserializeData, seralizeData } from '../utils/user-settings';

export const useUserPreferenceLocalStorage = <T>(
  key: string,
  storageKey: string,
  defaultValue: T,
  sync = false,
  session = false, // use sessionStorage if set to `true`
): [T, Dispatch<SetStateAction<T>>] => {
  // Mount status for safety state updates
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const storage = session ? sessionStorage : localStorage;
  const keyRef = useRef(key);
  const defaultValueRef = useRef(defaultValue);
  const [data, setData] = useState(() => {
    const valueInStorage =
      storage.getItem(storageKey) !== null && deserializeData(storage.getItem(storageKey));
    return valueInStorage?.hasOwnProperty(keyRef.current) &&
      valueInStorage[keyRef.current] !== undefined
      ? valueInStorage[keyRef.current]
      : defaultValueRef.current;
  });
  const dataRef = useRef<T>(data);
  dataRef.current = data;

  const storageUpdated = useCallback(
    (event: StorageEvent) => {
      if (mounted.current && event.storageArea === storage && event.key === storageKey) {
        const configMapData = deserializeData(event.newValue);
        const newData = configMapData?.[keyRef.current];

        if (newData !== undefined && seralizeData(newData) !== seralizeData(dataRef.current)) {
          setData(newData);
        }
      }
    },
    [storageKey, storage],
  );

  useEffect(() => {
    if (sync) {
      window.addEventListener('storage', storageUpdated);
    }
    return () => {
      if (sync) {
        window.removeEventListener('storage', storageUpdated);
      }
    };
  }, [storageUpdated, sync]);

  const updateData = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (action: SetStateAction<T>) => {
      const previousData = dataRef.current;
      const newState =
        typeof action === 'function' ? (action as (prevState: T) => T)(previousData) : action;
      const configMapData = deserializeData(storage.getItem(storageKey)) ?? {};
      if (
        newState !== undefined &&
        seralizeData(newState) !== seralizeData(configMapData?.[keyRef.current])
      ) {
        if (mounted.current) {
          setData(newState);
        }

        // Trigger update also when unmounted
        const dataToUpdate = {
          ...configMapData,
          ...{
            [keyRef.current]: newState,
          },
        };
        const newValue = seralizeData(dataToUpdate);

        // create a storage event to dispatch locally since browser windows do not fire the
        // storage event if the change originated from the current window
        const event = new StorageEvent('storage', {
          storageArea: storage,
          key: storageKey,
          newValue,
          oldValue: storage.getItem(storageKey),
          url: window.location.toString(),
        });

        try {
          // update storage
          storage.setItem(storageKey, newValue);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Error while updating local storage for key ${storageKey}`, err);
        }

        // dispatch storage event
        window.dispatchEvent(event);
      }
    },
    [storageKey, storage],
  );

  return [data, updateData];
};
